"use strict";
exports.__esModule = true;
var transaction_converter_1 = require("@iota/transaction-converter");
var Promise = require("bluebird");
var guards_1 = require("../../guards");
var _1 = require("./");
var MILESTONE_INTERVAL = 2 * 60 * 1000;
var ONE_WAY_DELAY = 1 * 60 * 1000;
var DEPTH = 6;
exports.isAboveMaxDepth = function (attachmentTimestamp, depth) {
    if (depth === void 0) { depth = DEPTH; }
    return attachmentTimestamp < Date.now() && Date.now() - attachmentTimestamp < depth * MILESTONE_INTERVAL - ONE_WAY_DELAY;
};
/**
 *
 * @method createIsPromotable
 *
 * @memberof module:core
 *
 * @param {Provider} provider - Network provider
 *
 * @param {number} [depth=6] - Depth up to which promotion is effective.
 *
 * @return {function} {@link #module_core.isPromotable `isPromotable`}
 */
exports.createIsPromotable = function (provider, depth) {
    if (depth === void 0) { depth = DEPTH; }
    var checkConsistency = _1.createCheckConsistency(provider);
    var getTrytes = _1.createGetTrytes(provider);
    /**
     * Checks if a transaction is _promotable_, by calling [`checkConsistency`]{@link #module_core.checkConsistency} and
     * verifying that `attachmentTimestamp` is above a lower bound.
     * Lower bound is calculated based on number of milestones issued
     * since transaction attachment.
     *
     * @example
     * #### Example with promotion and reattachments
     *
     * Using `isPromotable` to determine if transaction can be [_promoted_]{@link #module_core.promoteTransaction}
     * or should be [_reattached_]{@link #module_core.replayBundle}
     *
     * ```js
     * // We need to monitor inclusion states of all tail transactions (original tail & reattachments)
     * const tails = [tail]
     *
     * getLatestInclusion(tails)
     *   .then(states => {
     *     // Check if none of transactions confirmed
     *     if (states.indexOf(true) === -1) {
     *       const tail = tails[tails.length - 1] // Get latest tail hash
     *
     *       return isPromotable(tail)
     *         .then(isPromotable => isPromotable
     *           ? promoteTransaction(tail, 3, 14)
     *           : replayBundle(tail, 3, 14)
     *             .then(([reattachedTail]) => {
     *               const newTailHash = reattachedTail.hash
     *
     *               // Keeping track of all tail hashes to check confirmation
     *               tails.push(newTailHash)
     *
     *               // Promote the new tail...
     *             })
     *     }
     *   }).catch(err => {
     *     // ...
     *   })
     * ```
     *
     * @method isPromotable
     *
     * @memberof module:core
     *
     * @param {Hash} tail - Tail transaction hash
     * @param {Callback} [callback] - Optional callback
     *
     * @return {Promise}
     * @fulfil {boolean} Consistency state of transaction or co-consistency of transactions
     * @reject {Error}
     * - `INVALID_HASH`: Invalid hash
     * - `INVALID_DEPTH`: Invalid depth
     * - Fetch error
     */
    return function (tail, callback) {
        return Promise.resolve(guards_1.validate(guards_1.hashValidator(tail), guards_1.depthValidator(depth)))
            .then(function () {
            return Promise.all([
                checkConsistency(tail),
                getTrytes([tail]).then(function (_a) {
                    var trytes = _a[0];
                    return transaction_converter_1.asTransactionObject(trytes, tail).attachmentTimestamp;
                }),
            ]);
        })
            .then(function (_a) {
            var isConsistent = _a[0], attachmentTimestamp = _a[1];
            return isConsistent && exports.isAboveMaxDepth(attachmentTimestamp, depth);
        })
            .asCallback(callback);
    };
};
//# sourceMappingURL=createIsPromotable.js.map